import { Link } from 'react-router-dom'
import { Icon } from '../../components/icons/Icon'
import { StatusPill, type StatusPillVariant } from '../../components/StatusPill'
import { EntityCountChip } from '../../design-system/list-view/cells/EntityCountChip'
import { ForwardLookingCell } from '../../design-system/list-view/cells/ForwardLookingCell'
import type { ColumnDef } from '../../design-system/list-view/types'
import type { AccountStatus, AccountType, AccountView } from '../../types/account'

export const ACCOUNT_TYPE_LABEL: Record<AccountType, string> = {
  ULTIMATE_PARENT: 'Ultimate parent',
  HOLDING: 'Holding',
  INTERMEDIATE: 'Intermediate',
  OPERATING: 'Operating',
  DIVISION: 'Division',
}

export const ACCOUNT_STATUS_LABEL: Record<AccountStatus, string> = {
  ACTIVE: 'Active',
  PROSPECT: 'Prospect',
  INACTIVE: 'Inactive',
  ARCHIVED: 'Archived',
}

export const ACCOUNT_STATUS_VARIANT: Record<AccountStatus, StatusPillVariant> = {
  ACTIVE: 'success',
  PROSPECT: 'info',
  INACTIVE: 'neutral',
  ARCHIVED: 'muted',
}

const CASE_INFO_CONTENT = 'Arrives with the M6 Case module.'
const FISCAL_YEAR_INFO_CONTENT = 'Arrives with the M6 Case module (fiscal year is set at the case level).'

/**
 * Column order matches the IB-4 UX spec exactly. `Case` and `Fiscal year`
 * are forward-looking: present in every menu (sort/filter/columns) but
 * rendered disabled with the same tooltip, and their body cells render the
 * `ForwardLookingCell` muted en-dash rather than being absent.
 */
export function buildAccountColumns(): ColumnDef<AccountView>[] {
  return [
    {
      id: 'name',
      header: 'Account',
      width: 240,
      minWidth: 180,
      sortable: true,
      pinnable: true,
      toExportText: (row) => row.name,
      render: (row) => (
        <Link
          to={`/clients/accounts/${row.id}`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            color: 'var(--iw-accent)',
            fontWeight: 'var(--iw-weight-medium)' as never,
            textDecoration: 'none',
          }}
        >
          <Icon name="building2" size={14} />
          {row.name}
        </Link>
      ),
    },
    {
      id: 'legalEntityCount',
      header: 'Legal entity',
      width: 150,
      sortable: false,
      toExportText: (row) => String(row.legalEntityCount),
      render: (row) => (
        <EntityCountChip
          count={row.legalEntityCount}
          to={`/clients/legal-entities?accountId=${row.id}`}
          entityLabelSingular="legal entity"
          entityLabelPlural="legal entities"
        />
      ),
    },
    {
      id: 'case',
      header: 'Case',
      width: 90,
      sortable: false,
      disabledReason: CASE_INFO_CONTENT,
      headerInfo: { label: 'Case column info', content: CASE_INFO_CONTENT },
      toExportText: () => '',
      render: () => <ForwardLookingCell reason={CASE_INFO_CONTENT} />,
    },
    {
      id: 'fiscalYear',
      header: 'Fiscal year',
      width: 110,
      sortable: false,
      disabledReason: FISCAL_YEAR_INFO_CONTENT,
      headerInfo: { label: 'Fiscal year column info', content: FISCAL_YEAR_INFO_CONTENT },
      toExportText: () => '',
      render: () => <ForwardLookingCell reason={FISCAL_YEAR_INFO_CONTENT} />,
    },
    {
      id: 'type',
      header: 'Type',
      width: 140,
      sortable: true,
      toExportText: (row) => ACCOUNT_TYPE_LABEL[row.type],
      render: (row) => <span className="iw-pill iw-pill--neutral">{ACCOUNT_TYPE_LABEL[row.type]}</span>,
    },
    {
      id: 'country',
      header: 'Country',
      width: 110,
      sortable: false,
      toExportText: (row) => row.country,
      render: (row) => row.country,
    },
    {
      id: 'industry',
      header: 'Industry',
      width: 150,
      sortable: false,
      toExportText: (row) => row.industry,
      render: (row) => row.industry,
    },
    {
      id: 'status',
      header: 'Status',
      width: 120,
      sortable: true,
      toExportText: (row) => ACCOUNT_STATUS_LABEL[row.status],
      render: (row) => <StatusPill variant={ACCOUNT_STATUS_VARIANT[row.status]} label={ACCOUNT_STATUS_LABEL[row.status]} />,
    },
    {
      id: 'parentAccountName',
      header: 'Parent account',
      width: 200,
      sortable: false,
      defaultHidden: true,
      toExportText: (row) => row.parentAccountName ?? '',
      render: (row) => row.parentAccountName ?? <span style={{ color: 'var(--iw-text-subtle)' }}>—</span>,
    },
  ]
}

export const ACCOUNT_COLUMN_ORDER = [
  'name',
  'legalEntityCount',
  'case',
  'fiscalYear',
  'type',
  'country',
  'industry',
  'status',
  'parentAccountName',
]
